import React, { type PropsWithChildren } from 'react';
import ReactDOM from 'react-dom';

import { createRoot, type Root } from 'react-dom/client';
import {
	createIntl,
	createIntlCache,
	IntlProvider,
	type IntlShape,
	type MessageDescriptor,
} from 'react-intl';
// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazy, LazySuspense } from 'react-loosely-lazy';

import type { InlineCardDefinition } from '@atlaskit/adf-schema/inline-card';
import type { AnalyticsReactContextInterface } from '@atlaskit/analytics-next-stable-react-context';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions/extension-manifest';
import { ConfluenceAttributionIcon } from '@atlaskit/editor-common/quick-insert/confluence-attribution-icon';
import { JiraAttributionIcon } from '@atlaskit/editor-common/quick-insert/jira-attribution-icon';
import { messages as quickInsertMessages } from '@atlaskit/editor-common/quick-insert/messages';
import type { CreatePayload } from '@atlaskit/link-create/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { CreatePluginPresetsConfig } from '@atlassian/link-create-presets/plugin-presets';

import type { CreateFlags } from './common/types';
import { LinkCreateModalProvider } from './link-create-modal-provider';
import type { ManifestProps } from './types';
import { confluenceExtensionMessages } from './ui/confluence-extension-messages';
import { ErrorBoundary } from './ui/error-boundary';
import { jiraExtensionMessages } from './ui/jira-extension-messages';
import { keywords } from './ui/keywords';
let reactRoots = new WeakMap<Element, Root>();

/** Mounts `element` into `mountPoint`: uses the React 18/19 `createRoot` API when `nike_r19_render_unmount` is on, else the legacy render path. */
const renderToMountPoint = (element: React.ReactElement, mountPoint: Element) => {
	if (fg('nike_r19_render_unmount')) {
		let root = reactRoots.get(mountPoint);

		if (!root) {
			root = createRoot(mountPoint);
			reactRoots.set(mountPoint, root);
		}

		root.render(element);
	} else {
		ReactDOM.render(element, mountPoint);
	}
};

/** Unmounts the tree at `mountPoint`: uses `root.unmount()` when `nike_r19_render_unmount` is on, else the legacy unmount path. */
const unmountFromMountPoint = (mountPoint: Element) => {
	if (fg('nike_r19_render_unmount')) {
		const root = reactRoots.get(mountPoint);

		if (root) {
			root.unmount();
			reactRoots.delete(mountPoint);
		}
	} else {
		ReactDOM.unmountComponentAtNode(mountPoint);
	}
};

// as const so that values can be looked up for i18n in ./ui/messages/keywords
const JIRA_KEYWORDS = ['create', 'issue', 'ticket', 'raise', 'new'] as const;
const CONFLUENCE_KEYWORDS = ['create', 'page', 'new', 'space', 'publish'] as const;

// categories will put us into tabs in the quick insert browser (the plus menu)
const JIRA_CATEGORIES = ['development', 'external-content'];
const CONFLUENCE_CATEGORIES = ['confluence-content'];

export const manifest = ({
	intl,
	intlLocale,
	defaultCloudId,
	analyticsContext,
	location,
	gatewayBaseUrl,
	flags,
	defaultFormValues,
	pageContext,
	allowConfluenceEditViewEmbed,
}: ManifestProps): ExtensionManifest => {
	const messageFormatter = intl ?? createIntl({ locale: intlLocale ?? 'en' }, createIntlCache());
	const formatMessage = (
		descriptor: Pick<MessageDescriptor, 'id' | 'description'> & {
			defaultMessage: string;
		},
	): string => {
		return messageFormatter.formatMessage(descriptor);
	};

	return {
		title: 'Link create editor extension',
		type: 'extension',
		key: 'com.atlassian.linking-platform.create',
		description:
			'Configures quick insert commands for creating objects, like Jira issues and Confluence pages as links',
		icons: {
			'48': () =>
				import(
					/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon" */ './common/ui/icons/plussquare'
				).then((mod) => mod.default),
		},
		modules: {
			quickInsert: [
				{
					key: 'linking-platform-create-jira-issue',
					preview: {
						image: {
							dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVIH.png',
							light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVLP.png',
						},
						attribution: {
							name: formatMessage(quickInsertMessages.previewAttributionJira),
							icon: JiraAttributionIcon,
						},
					},
					title: formatMessage(
						isExperimentEnabled('platform_editor_slash_command')
							? jiraExtensionMessages.extensionTitleWorkItem
							: fg('jira-issue-terminology-update-m4')
								? jiraExtensionMessages.extensionTitleIssueTerminologyRefreshM4
								: jiraExtensionMessages.extensionTitle,
					),
					description: formatMessage(
						fg('jira-issue-terminology-update-m4')
							? jiraExtensionMessages.extensionDescriptionIssueTerminologyRefreshM4
							: jiraExtensionMessages.extensionDescription,
					),
					featured: true,
					category: 'create',
					categories: JIRA_CATEGORIES,
					keywords: ['jira', ...JIRA_KEYWORDS.map((keyword) => formatMessage(keywords[keyword]))],
					icon: () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon" */ './common/ui/icons/plussquare'
						).then((mod) => mod.default),
					action: async () =>
						showCreateModal({
							intl,
							intlLocale,
							defaultCloudId,
							analyticsContext,
							entityKey: 'jira-issue',
							gatewayBaseUrl,
							location,
							flags,
							defaultFormValues,
						}),
				},
				{
					key: 'linking-platform-create-confluence-page',
					preview: {
						image: {
							dark: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVJN.png',
							light: 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT12OVNG.png',
						},
						attribution: {
							name: formatMessage(quickInsertMessages.previewAttributionConfluence),
							icon: ConfluenceAttributionIcon,
						},
					},
					title: formatMessage(
						isExperimentEnabled('platform_editor_slash_command')
							? confluenceExtensionMessages.extensionTitlePage
							: confluenceExtensionMessages.extensionTitle,
					),
					description: formatMessage(confluenceExtensionMessages.extensionDescription),
					featured: true,
					category: 'create',
					categories: CONFLUENCE_CATEGORIES,
					keywords: [
						'confluence',
						...CONFLUENCE_KEYWORDS.map((keyword) => formatMessage(keywords[keyword])),
					],
					icon: () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_@atlassian/editor-extension-link-create-icon-page" */ '@atlaskit/icon/core/page'
						).then((mod) => mod.default),
					action: async () => {
						// Only do one step page creation if we are
						// 1. in the full page editor
						// 2. not in a blogpost
						// 3. not using Safari
						const shouldUseOneStepCreate =
							location === 'confluence-page' &&
							pageContext?.isFullPageEditor &&
							!pageContext?.isBlogpost &&
							!getBrowserInfo().safari;

						if (shouldUseOneStepCreate) {
							return oneStepCreate({
								intl,
								intlLocale,
								defaultCloudId,
								analyticsContext,
								gatewayBaseUrl,
								flags,
								defaultFormValues,
							});
						}

						return showCreateModal({
							intl,
							intlLocale,
							defaultCloudId,
							analyticsContext,
							entityKey: 'confluence-page',
							gatewayBaseUrl,
							location,
							flags,
							defaultFormValues,
							allowConfluenceEditViewEmbed,
						});
					},
				},
			],
		},
	};
};

const DEFAULT_LOCALE = 'en';
const LINK_CREATE_REFERENCE_MOUNT_POINT_ID = 'link-create-mount-point';

const getMountPoint = (): HTMLElement => {
	let modalEl = document.getElementById(`${LINK_CREATE_REFERENCE_MOUNT_POINT_ID}`);

	if (modalEl === null) {
		modalEl = document.createElement('div');
		modalEl.id = LINK_CREATE_REFERENCE_MOUNT_POINT_ID;
		document.body.appendChild(modalEl);
	}

	return modalEl;
};

const AnalyticsProviderWrapper = ({
	children,
	value,
}: PropsWithChildren<{ value?: AnalyticsReactContextInterface }>) => {
	if (value === undefined) {
		return <>{children}</>;
	}
	return <LinkCreateModalProvider value={value}>{children}</LinkCreateModalProvider>;
};

const LazyEditorExtensionLinkCreate = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian_editor-extension-link-create-component" */
		'./ui/index'
	).then(({ EditorExtensionLinkCreate }) => ({
		default: EditorExtensionLinkCreate,
	})),
);

const LazyOneStepLinkCreate = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian_one-step-link-create" */
		'./inline-create-one-step/main'
	).then(({ OneStepLinkCreate }) => ({
		default: OneStepLinkCreate,
	})),
);

/*
 * Rather than rendering a modal and requesting user input, this function
 * creates a new page and immediately inserts it without any user input.
 * The title is generated on the backend so the new page is just created
 * and inserted as a smartlink. This is part of the Confluence Inline Create
 * One Step experiment.
 */
const oneStepCreate = ({
	intl,
	intlLocale,
	defaultCloudId,
	gatewayBaseUrl,
	analyticsContext,
	flags,
	defaultFormValues,
}: {
	analyticsContext?: AnalyticsReactContextInterface;
	defaultCloudId: string;
	defaultFormValues?: CreatePluginPresetsConfig['defaultFormValues'];
	flags?: CreateFlags;
	gatewayBaseUrl?: string;
	intl?: IntlShape;
	intlLocale?: string;
}): Promise<InlineCardDefinition | void> => {
	return new Promise<InlineCardDefinition | void>(async (res) => {
		const mountPoint = getMountPoint();

		const handleCreate = (payload: CreatePayload) => {
			res({
				type: 'inlineCard',
				attrs: { url: payload.url },
			});

			// Open the draft in a new tab and show the success flag
			const parentId = defaultFormValues?.['confluence-page']?.parentId ?? '';
			window.name = parentId;

			// createdFrom param indicates to the new draft window to show a success flag on load
			const url = new URL(payload.linkToOpen ?? payload.url);
			url.searchParams.set('createdFrom', parentId);

			window.open(url, '_blank');
		};

		const handleComplete = () => {
			unmountFromMountPoint(mountPoint);
		};

		renderToMountPoint(
			<AnalyticsProviderWrapper value={analyticsContext}>
				<IntlProvider
					messages={intl?.messages ?? {}}
					locale={intl?.locale ?? intlLocale ?? 'en'}
					defaultLocale={DEFAULT_LOCALE}
					onError={intl?.onError}
				>
					<AnalyticsContext
						data={{
							source: 'inlineCreateOneStep',
							attributes: {
								component: 'editorExtensionLinkCreate',
								location: 'confluence-page',
								triggeredFrom: 'quick-insert',
							},
						}}
					>
						<ErrorBoundary
							flags={flags}
							onError={() => {
								/**
								 * If something goes wrong, resolve the promise to editor,
								 * and unmount the component mountpoint
								 */
								res();
								handleComplete();
							}}
						>
							<LazySuspense fallback={null}>
								<LazyOneStepLinkCreate
									onCreate={handleCreate}
									gatewayBaseUrl={gatewayBaseUrl}
									defaultCloudId={defaultCloudId}
									defaultFormValues={defaultFormValues}
								/>
							</LazySuspense>
						</ErrorBoundary>
					</AnalyticsContext>
				</IntlProvider>
			</AnalyticsProviderWrapper>,
			mountPoint,
		);
	});
};

const showCreateModal = ({
	intl,
	intlLocale,
	defaultCloudId,
	entityKey,
	gatewayBaseUrl,
	location,
	analyticsContext,
	flags,
	defaultFormValues,
	allowConfluenceEditViewEmbed,
}: {
	allowConfluenceEditViewEmbed?: boolean;
	analyticsContext?: AnalyticsReactContextInterface;
	defaultCloudId: string;
	defaultFormValues?: CreatePluginPresetsConfig['defaultFormValues'];
	entityKey: string;
	flags?: CreateFlags;
	gatewayBaseUrl?: string;
	intl?: IntlShape;
	intlLocale?: string;
	location?: string;
}): Promise<InlineCardDefinition | void> =>
	new Promise(async (res) => {
		const mountPoint = getMountPoint();

		const handleCreate = (payload: CreatePayload) => {
			res({
				type: 'inlineCard',
				attrs: { url: payload.url },
			});
		};

		const handleCancel = () => {
			res();
		};

		const handleCloseComplete = () => {
			unmountFromMountPoint(mountPoint);
		};

		renderToMountPoint(
			<AnalyticsProviderWrapper value={analyticsContext}>
				<IntlProvider
					messages={intl?.messages ?? {}}
					locale={intl?.locale ?? intlLocale ?? 'en'}
					defaultLocale={DEFAULT_LOCALE}
					onError={intl?.onError}
				>
					<AnalyticsContext
						data={{
							attributes: {
								component: 'editorExtensionLinkCreate',
								location,
								triggeredFrom: 'quick-insert',
							},
						}}
					>
						<ErrorBoundary
							flags={flags}
							onError={() => {
								/**
								 * If something goes wrong, resolve the promise to editor,
								 * and unmount the component mountpoint
								 */
								res();
								handleCloseComplete();
							}}
						>
							<LazySuspense fallback={null}>
								<LazyEditorExtensionLinkCreate
									defaultCloudId={defaultCloudId}
									gatewayBaseUrl={gatewayBaseUrl}
									defaultFormValues={defaultFormValues}
									entityKey={entityKey}
									onCreate={handleCreate}
									onCancel={handleCancel}
									onCloseComplete={handleCloseComplete}
									flags={flags}
									allowConfluenceEditViewEmbed={allowConfluenceEditViewEmbed}
								/>
							</LazySuspense>
						</ErrorBoundary>
					</AnalyticsContext>
				</IntlProvider>
			</AnalyticsProviderWrapper>,
			mountPoint,
		);
	});
